import { usePendingImage } from "@ui/hooks/usePendingImage";
import { VideoPreview } from "@ui/molecules/VideoPreview/VideoPreview";
import { createSignal, For, Show } from "solid-js";
import type { VideoEvidence } from "../../../ChatEventTypes";
import { videoEvidenceHlsSource } from "../../../utils/videoEvidenceHlsSource";
import "./VideoPanelCarousel.css";
import { VideoCarouselOverlay } from "../VideoCarouselOverlay";
import { ScrollButtons } from "./components/ScrollButtons";
import { ToggleOverlay } from "./components/ToggleOverlay";
import { VideoPanelCarouselTrack } from "./components/VideoPanelCarouselTrack";
import { VideoPanelCarouselViewport } from "./components/VideoPanelCarouselViewport";
import { WatchFullVideoButton } from "./components/WatchFullVideoButton";
import { isExternalBrowsablePostUrl } from "../../../utils/platformPostUrl";
import { useCarousel } from "./useCarousel";

type Props = {
	activeSlide: number;
	setActiveSlide: (slide: number) => void;
	videoEvidence: VideoEvidence[];
	boardId?: string;
	onClose: () => void;
	onEditClip?: (clipId: string) => void;
	showAddToBoardButton?: boolean;
	projectId?: string;
	/** When false, slide changes apply instantly (no transform transition). */
	animateSlideChanges?: boolean;
	/** Increment to apply the next slide change instantly (e.g. mosaic clip click). */
	instantJumpToken?: number;
	/** When false, the active clip does not start playing until the user presses play. */
	autoPlay?: boolean;
};

/**
 * New simplified carousel component for the video panel.
 */
export const VideoPanelCarousel = (props: Props) => {
	const [autoPlayNextSlide, setAutoPlayNextSlide] = createSignal(true);

	// Manual nav (scroll buttons) stops auto-advance; clip-end advance uses props.setActiveSlide directly.
	const setActiveSlideManual = (slide: number) => {
		setAutoPlayNextSlide(false);
		props.setActiveSlide(slide);
	};

	// Pass reactive props — never `{ ...props }` or activeSlide is snapshotted as a plain number.
	const carousel = useCarousel(props, { autoPlayNextSlide });

	const isSocialMediaPost = () =>
		isExternalBrowsablePostUrl(
			props.videoEvidence[props.activeSlide]?.post_url,
		);
	return (
		<div class="video-panel-carousel-root">
			<div class="video-panel-carousel">
				<VideoPanelCarouselViewport
					isActivePortrait={carousel.isActivePortrait}
				>
					<VideoPanelCarouselTrack
						activeSlide={props.activeSlide}
						animateSlides={carousel.shouldAnimateSlides}
					>
						<For each={props.videoEvidence}>
							{(videoEvidence, index) => {
								const slideIndex = index();
								const isActive = () => slideIndex === props.activeSlide;
								const isSlidePortrait = () =>
									isActive()
										? carousel.isActivePortrait()
										: carousel.isPortraitVideo(videoEvidence);
								const thumbnail = usePendingImage(
									() => videoEvidence.thumbnail_url,
								);
								const previewAspectRatioStyle = () =>
									videoEvidence.aspect_ratio != null &&
									videoEvidence.aspect_ratio > 0
										? { "aspect-ratio": String(videoEvidence.aspect_ratio) }
										: undefined;
								return (
									<div class="video-panel-carousel__slide">
										<Show
											when={thumbnail.src()}
											fallback={
												<div
													class="video-panel-carousel__thumbnail-backdrop video-panel-carousel__thumbnail-backdrop--fallback"
													aria-hidden="true"
												/>
											}
										>
											{(thumbnailUrl) => (
												<div
													class="video-panel-carousel__thumbnail-backdrop"
													style={{
														"background-image": `url(${thumbnailUrl()})`,
													}}
													aria-hidden="true"
												/>
											)}
										</Show>
										<VideoPreview
											portrait={isSlidePortrait()}
											playTrigger="none"
											autoPlay={
												isActive() &&
												(props.autoPlay === false
													? !carousel.userPaused()
													: true)
											}
											forcePause={!isActive() || carousel.userPaused()}
											forceMuteAudio={!isActive()}
											disabled={!isActive()}
											loop={false}
											style={previewAspectRatioStyle()}
											videoUrl={videoEvidence.video_url}
											hlsSource={videoEvidenceHlsSource(videoEvidence)}
											startSeconds={Math.round(videoEvidence.start_time)}
											endSeconds={Math.round(videoEvidence.end_time)}
											showControls={false}
											showDuration={false}
											playbackSeekRequest={() =>
												carousel.seekRequestForSlide(slideIndex)
											}
											onVideoDimensions={(size) =>
												carousel.onSlideVideoDimensions(slideIndex, size)
											}
											onPlaybackTime={(seconds) =>
												carousel.onClipPlaybackTime(
													slideIndex,
													videoEvidence,
													seconds,
												)
											}
											onPlay={() => carousel.onActiveVideoPlay(slideIndex)}
											onPause={() => carousel.onActiveVideoPause(slideIndex)}
										/>
									</div>
								);
							}}
						</For>
					</VideoPanelCarouselTrack>
					<div class="overlay">
						<ToggleOverlay
							overlayContentVisible={carousel.overlayContentVisible}
							setOverlayContentVisible={carousel.setOverlayContentVisible}
						/>
						<Show when={carousel.overlayContentVisible()}>
							<VideoCarouselOverlay
								boardId={props.boardId}
								activeSlideIndex={props.activeSlide}
								videoEvidence={props.videoEvidence}
								currentVideoProgressPercent={carousel.currentVideoProgressPercent()}
								isPlaying={carousel.mediaPlaying()}
								onTogglePlay={() => carousel.setUserPaused((paused) => !paused)}
								onSeek={carousel.seekActiveClip}
								onSelectVideo={setActiveSlideManual}
								onClose={props.onClose}
								onAddToBoard={carousel.onAddToBoard}
								clipInBoard={carousel.clipInBoard}
								membershipLoading={carousel.clipMembershipLoading}
								onEditClip={
									props.onEditClip
										? () => {
												const clipId =
													props.videoEvidence[props.activeSlide]?.video_id;
												if (!clipId) {
													return;
												}
												carousel.setUserPaused(true);
												props.onEditClip(clipId);
											}
										: undefined
								}
								volume={carousel.volume}
							/>
						</Show>
					</div>
					<ScrollButtons
						activeSlide={props.activeSlide}
						setActiveSlide={setActiveSlideManual}
						videoEvidence={props.videoEvidence}
					/>
				</VideoPanelCarouselViewport>
			</div>
			<Show when={isSocialMediaPost()}>
				<div class="video-panel-carousel__footer">
					<WatchFullVideoButton
						videoUrl={props.videoEvidence[props.activeSlide]?.post_url}
					/>
				</div>
			</Show>
		</div>
	);
};
